import type React from "react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { AllocationLevel, TargetRow } from "../../lib/allocation";
import { Modal } from "../ui/Modal";

interface TargetFormModalProps {
  open: boolean;
  onClose: () => void;
  level: AllocationLevel;
  keys: readonly string[];
  initial: TargetRow[] | null;
  onSubmit: (rows: TargetRow[]) => Promise<unknown>;
  submitting?: boolean;
}

export function TargetFormModal({
  open,
  onClose,
  level,
  keys,
  initial,
  onSubmit,
  submitting,
}: TargetFormModalProps) {
  const { t } = useTranslation();
  const editableKeys = keys.slice(0, -1);
  const autoKey = keys[keys.length - 1] as string;

  const initialMap = useMemo(() => {
    const m: Record<string, number> = Object.fromEntries(keys.map((k) => [k, 0]));
    if (initial) for (const r of initial) m[r.key] = r.targetBp;
    return m;
  }, [initial, keys]);

  const [values, setValues] = useState<Record<string, number>>(initialMap);
  useEffect(() => setValues(initialMap), [initialMap]);

  const editableSum = editableKeys.reduce((s, k) => s + (values[k] ?? 0), 0);
  const autoBp = 10000 - editableSum;
  const autoNegative = autoBp < 0;
  const autoOverflow = autoBp > 10000;
  const editableNegative = editableKeys.some((k) => (values[k] ?? 0) < 0);
  const saveDisabled = autoNegative || autoOverflow || editableNegative;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (saveDisabled) return;
    const rows: TargetRow[] = keys.map((k) =>
      k === autoKey ? { key: k, targetBp: autoBp } : { key: k, targetBp: values[k] ?? 0 },
    );
    await onSubmit(rows);
  }

  return (
    <Modal open={open} onClose={onClose} title={t(`allocation.target.edit_${level.toLowerCase()}`)}>
      <form onSubmit={handleSubmit} className="space-y-3">
        {editableKeys.map((k) => (
          <label key={k} className="flex items-center justify-between gap-3">
            <span className="text-sm">
              {t(`allocation.${level === "TOP" ? "top_class_label" : "sub_kind_label"}.${k}`)}
            </span>
            <span className="flex items-center gap-2">
              <input
                type="number"
                min={0}
                max={10000}
                step={1}
                value={values[k] ?? 0}
                onChange={(e) =>
                  setValues((v) => ({ ...v, [k]: Math.trunc(Number(e.target.value)) }))
                }
                className="w-24 border rounded px-2 py-1 text-right font-mono"
              />
              <span className="text-xs text-slate-500">
                bp ({((values[k] ?? 0) / 100).toFixed(2)} %)
              </span>
            </span>
          </label>
        ))}
        <div className="flex items-center justify-between gap-3 bg-slate-50 px-2 py-1 rounded">
          <span className="text-sm">
            {t(`allocation.${level === "TOP" ? "top_class_label" : "sub_kind_label"}.${autoKey}`)}{" "}
            <span className="text-xs text-slate-500">({t("allocation.target.auto_hint")})</span>
          </span>
          <span className="flex items-center gap-2">
            <span className={`font-mono ${autoNegative || autoOverflow ? "text-red-700" : ""}`}>
              {autoBp} bp ({(autoBp / 100).toFixed(2)} %)
            </span>
          </span>
        </div>
        <p className="text-xs text-slate-600">
          {t("allocation.target.sum_label")}: 10000 / 10000{" "}
          {autoNegative
            ? `— ${t("allocation.target.sum_overflow")}`
            : autoOverflow
              ? `— ${t("allocation.target.sum_underflow")}`
              : "✓"}
        </p>
        <div className="flex justify-end gap-2">
          <button type="button" className="px-3 py-1 border rounded" onClick={onClose}>
            {t("common.cancel")}
          </button>
          <button
            type="submit"
            disabled={saveDisabled || submitting}
            className="px-3 py-1 bg-blue-600 text-white rounded disabled:opacity-50"
          >
            {t("common.save")}
          </button>
        </div>
      </form>
    </Modal>
  );
}
